<script lang="ts">
  import { getContext } from "svelte"
  import AULink from "component_library/components/svelte/ui/AULink.svelte"
  import AUModal from "component_library/components/svelte/ui/AUModal.svelte"
  import AUAlert from "component_library/components/svelte/ui/AUAlert.svelte"
  import AUText from "component_library/components/svelte/ui/AUText.svelte"
  import AUStack from "component_library/components/svelte/ui/AUStack.svelte"
  import AUButton from "component_library/components/svelte/ui/AUButton.svelte"
  import PriceRating from "component_library/components/svelte/PriceRating.svelte"
  import Svg from "./Svg.svelte"
  import { getFlowsContext } from "website_script/contexts/flows"
  import type { Texts } from "website_script/types/texts"
  import { Settings } from "website_script/types/settings"
  import { ButtonConfig } from "website_script/types/widgets"
  import IntegrationTypes from "website_script/constants/IntegrationTypes"
  import insightsLogoBgVariant from "website_script/assets/images/insights-logo-bg-variant.png"
  import logo from "../../icons/autouncleLogoUnconstrained.svg?raw"
  import info from "@autouncle/icons/dist/assets/info.svg?raw"
  import { getDealerThemeTokensStyle } from "website_script/lib/dealerTheme"
  import { leadFlowIcon } from "conversion_flows/types/leadFlowIcons"
  import { trackFunction } from "website_script/lib/tracking"
  import { GetCarHighlightsForWidgetResult } from "website_script/operations/getCarHighlightsForWidget"
  import { autouncleBrandTheme } from "lib/constants"

  export let car: NonNullable<Settings["car"]>
  export let carHighlightsForWidget: NonNullable<
    GetCarHighlightsForWidgetResult["conversionBoxCarHighlights"]
  >["highlights"]
  export let insightsBasisText: string
  export let buttons: ButtonConfig[]

  let modal: AUModal

  const {
    close,
    conversionBox: { learnMore, insights, by },
  } = getContext<Texts>("texts")
  const { priceRatingInfoUrl, carRatingText } = getContext<Settings>("settings")
  const { triggerFlow } = getFlowsContext()

  $: priceRatingHighlight = carHighlightsForWidget.find(
    (highlight) => highlight.name === "good_price_rating"
  )
  $: carHighlights = carHighlightsForWidget.filter(
    (highlight) => highlight.name !== "good_price_rating"
  )
  // Currently in the ConversionBox it only makes sense to show positive price ratings
  const rating = car.au_rating == null || car.au_rating < 3 ? 0 : car.au_rating
  const ratingText = carRatingText ?? ""
  const insightsHeaderStyle = `
      background:
      url("${insightsLogoBgVariant}") 40% top no-repeat,
      var(--au-background-default);
      background-size: 132px auto, auto;
  `
  const _triggerFlow = (buttonConfig: ButtonConfig) => {
    modal.close()
    triggerFlow(buttonConfig, IntegrationTypes.INSIGHTS_VIEW, undefined)
    trackFunction({
      entity: "insights-view",
      element: "flow_button",
      action: "clicked",
      flow: buttonConfig.flow_type,
      type: "interacted_with_cta",
      carRating: rating,
    })
  }

  export function openModal() {
    trackFunction({
      entity: "insights-view",
      action: "displayed",
      type: "micro_proceeding",
      carRating: rating,
    })
    modal.open()
  }
</script>

<AUModal
  bind:this={modal}
  closeLabel={close}
  showFooter={Boolean(buttons.length)}
  title={insights}
  id="conversion-box-insights-details"
>
  <div style={getDealerThemeTokensStyle(autouncleBrandTheme)}>
    <div style={insightsHeaderStyle} class="au-insights-details__header">
      <AUText font="heading-2">{insights}</AUText>
      <AUText font="body-small" weight="regular" color="body"
        >{by} <Svg data={logo} height="10" /></AUText
      >
    </div>
    <div class="au-insights-details__content">
      <AUAlert severity="info">
        <Svg slot="icon" data={info} aria-hidden="true" />
        {insightsBasisText}
      </AUAlert>

      {#if rating && ratingText}
        <AUStack gap="2xs">
          <AUStack
            layout="horizontal"
            gap="xs"
            alignItems="center"
            justifyContent="space-between"
          >
            <PriceRating layout="horizontal" {rating} {ratingText} />

            {#if priceRatingInfoUrl}
              <div class="au-insights-details__learn-more-wrapper">
                <AULink href={priceRatingInfoUrl} target="_blank">
                  <AUText font="body-small" color="link">
                    {learnMore}
                  </AUText>
                </AULink>
              </div>
            {/if}
          </AUStack>

          {#if priceRatingHighlight}
            <AUText font="body-xsmall" color="default"
              >{priceRatingHighlight.text}</AUText
            >
          {/if}
        </AUStack>
      {/if}

      {#each carHighlights as carHighlight (carHighlight.title)}
        <AUStack gap="2xs">
          {#if carHighlight.title}
            <AUText font="body-small" weight="bold" color="positive"
              >{carHighlight.title}</AUText
            >
          {/if}
          <AUText font="body-xsmall" color="default">{carHighlight.text}</AUText
          >
        </AUStack>
      {/each}
    </div>
  </div>
  <AUStack slot="footer" gap="xs">
    {#each buttons as buttonConfig}
      <AUButton
        fullWidth
        color={buttonConfig.primary ? "primary" : "secondary"}
        onClick={() => _triggerFlow(buttonConfig)}
      >
        <Svg
          alt={buttonConfig.text}
          aria-hidden="true"
          data={leadFlowIcon(buttonConfig.icon)}
          slot="leftIcon"
        />

        {buttonConfig.text}
      </AUButton>
    {/each}
  </AUStack>
</AUModal>

<style>
  .au-insights-details__header {
    padding: var(--au-spacing-md) 0;
  }

  .au-insights-details__content {
    display: flex;
    flex-direction: column;
    gap: 64px;
  }

  .au-insights-details__learn-more-wrapper {
    flex-shrink: 0;
  }
</style>
